import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { ArrowUpRight, Snowflake, Sparkles } from 'lucide-react';
import { THERMACUBE_CHECKOUT_URL } from '../constants/storeUrls';
import {
  THERMACUBE_HERO_STILL,
  THERMACUBE_PRICE_INR,
  THERMACUBE_SIGNATURE_MONTAGE,
} from '../constants/thermaCube';

const EASE = [0.22, 1, 0.36, 1];
const RESOLVE_AFTER_MS = 1600;
const MONTAGE_MS = 380;
const MONTAGE_PASSES = 2;
/** Pause on hero still before fast-cut montage loops again. */
const HOLD_AFTER_MONTAGE_MS = 4200;

function formatInr(n) {
  return `\u20B9${Math.round(n).toLocaleString('en-IN')}`;
}

const ICE_SHARD_COUNT = 22;

function IceShard({ i, reduceMotion }) {
  const left = `${(i * 17 + 7) % 100}%`;
  const delay = (i * 0.35) % 6;
  const duration = 7 + (i % 5) * 1.2;
  if (reduceMotion) {
    return (
      <span
        className="absolute w-2 h-6 rounded-sm bg-cyan-200/15 border border-white/10"
        style={{ left, top: `${15 + (i % 8) * 8}%`, transform: 'rotate(12deg)' }}
        aria-hidden
      />
    );
  }
  return (
    <span
      className="absolute w-1.5 h-4 sm:w-2 sm:h-5 rounded-[2px] bg-gradient-to-b from-cyan-100/50 to-cyan-300/20 border border-white/25 shadow-[0_0_12px_rgba(180,230,255,0.15)] motion-safe:animate-ice-fall"
      style={{
        left,
        top: '-8%',
        animationDuration: `${duration}s`,
        animationDelay: `${delay}s`,
        transform: `rotate(${i * 14}deg)`,
      }}
      aria-hidden
    />
  );
}

/** Minimal seated figure — slumped, overwhelmed (SVG). */
function FrustratedFigure({ className }) {
  return (
    <svg className={className} viewBox="0 0 220 260" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <ellipse cx="110" cy="218" rx="78" ry="26" className="fill-white/[0.05]" />
      <circle cx="110" cy="72" r="30" className="fill-zinc-500 stroke-zinc-600" strokeWidth="2" />
      <path d="M92 64h-2M130 64h-2" className="stroke-zinc-800" strokeWidth="3" strokeLinecap="round" />
      <path d="M96 86c6 5 22 5 28 0" className="stroke-zinc-800" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M110 102v28"
        className="stroke-zinc-500"
        strokeWidth="14"
        strokeLinecap="round"
      />
      <path
        d="M76 118c-16 28-22 62-14 92M144 118c16 28 22 62 14 92"
        className="stroke-zinc-500"
        strokeWidth="12"
        strokeLinecap="round"
      />
      <path
        d="M110 130c-28 8-44 36-48 68M110 130c28 8 44 36 48 68"
        className="stroke-zinc-600/90"
        strokeWidth="10"
        strokeLinecap="round"
      />
    </svg>
  );
}

function HotWaterBottle({ className }) {
  const gid = useId().replace(/:/g, '');
  return (
    <svg className={className} viewBox="0 0 72 108" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <defs>
        <linearGradient id={gid} x1="36" y1="8" x2="36" y2="100" gradientUnits="userSpaceOnUse">
          <stop stopColor="#c75a4a" />
          <stop offset="1" stopColor="#7c2d2d" />
        </linearGradient>
      </defs>
      <path
        d="M24 32c0-12 10-20 24-20s24 8 24 20v60c0 14-10 24-24 24S24 106 24 92V32z"
        fill={`url(#${gid})`}
        stroke="rgba(0,0,0,0.35)"
        strokeWidth="1.5"
      />
      <rect x="28" y="10" width="32" height="14" rx="6" fill="#3f2020" />
      <path d="M32 6h24" stroke="#2a1515" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

function IcePack({ className }) {
  return (
    <div
      className={`relative rounded-lg border border-cyan-200/40 bg-gradient-to-br from-cyan-100/25 to-cyan-400/10 shadow-[0_0_20px_rgba(100,200,255,0.12)] backdrop-blur-sm ${className}`}
    >
      <Snowflake className="w-4 h-4 text-cyan-200/70 absolute right-1.5 top-1.5" strokeWidth={1.25} aria-hidden />
    </div>
  );
}

export function SignatureThermaCubeStory() {
  const reduceMotion = useReducedMotion();
  const stageRef = useRef(null);
  const inView = useInView(stageRef, { once: true, amount: 0.32 });
  const [resolved, setResolved] = useState(false);
  const [montageIdx, setMontageIdx] = useState(0);
  const [montageDone, setMontageDone] = useState(false);

  useEffect(() => {
    const montage = Array.isArray(THERMACUBE_SIGNATURE_MONTAGE) ? THERMACUBE_SIGNATURE_MONTAGE : [];
    [...montage, THERMACUBE_HERO_STILL].filter(Boolean).forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    if (reduceMotion) {
      setResolved(true);
      return undefined;
    }
    if (!inView) return undefined;
    const id = window.setTimeout(() => setResolved(true), RESOLVE_AFTER_MS);
    return () => window.clearTimeout(id);
  }, [inView, reduceMotion]);

  useEffect(() => {
    if (!resolved) {
      setMontageIdx(0);
      setMontageDone(false);
      return undefined;
    }
    if (reduceMotion) {
      setMontageDone(true);
      return undefined;
    }
    if (montageDone) {
      return undefined;
    }
    const montage = Array.isArray(THERMACUBE_SIGNATURE_MONTAGE) ? THERMACUBE_SIGNATURE_MONTAGE : [];
    const n = montage.length;
    if (n === 0) {
      setMontageDone(true);
      return undefined;
    }
    let step = 0;
    const id = window.setInterval(() => {
      step += 1;
      if (step >= n * MONTAGE_PASSES) {
        setMontageDone(true);
        window.clearInterval(id);
        return;
      }
      setMontageIdx(step % n);
    }, MONTAGE_MS);
    return () => window.clearInterval(id);
  }, [resolved, reduceMotion, montageDone]);

  useEffect(() => {
    if (reduceMotion || !resolved || !montageDone) return undefined;
    const montage = Array.isArray(THERMACUBE_SIGNATURE_MONTAGE) ? THERMACUBE_SIGNATURE_MONTAGE : [];
    if (montage.length === 0) return undefined;
    const id = window.setTimeout(() => {
      setMontageDone(false);
      setMontageIdx(0);
    }, HOLD_AFTER_MONTAGE_MS);
    return () => window.clearTimeout(id);
  }, [resolved, reduceMotion, montageDone]);

  const montage = Array.isArray(THERMACUBE_SIGNATURE_MONTAGE) ? THERMACUBE_SIGNATURE_MONTAGE : [];
  const productSrc =
    montageDone || montage.length === 0
      ? THERMACUBE_HERO_STILL
      : montage[montageIdx % montage.length] ?? THERMACUBE_HERO_STILL;
  const productKey = montageDone ? 'hold' : `m-${montageIdx}`;

  return (
    <section
      id="signature"
      className="relative py-20 md:py-28 px-5 sm:px-8 overflow-hidden bg-[#06080a] text-white border-b border-white/[0.06]"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_55%_at_30%_20%,rgba(44,95,84,0.18),transparent_55%)]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_50%_40%_at_85%_75%,rgba(201,169,98,0.08),transparent_50%)]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#0a1620]/88 via-[#06080a]/45 to-transparent z-[1]"
        aria-hidden
      />
      <div className="pointer-events-none absolute inset-0 overflow-hidden z-[1]" aria-hidden>
        {Array.from({ length: ICE_SHARD_COUNT }, (_, i) => (
          <IceShard key={i} i={i} reduceMotion={reduceMotion} />
        ))}
      </div>

      <div className="relative z-10 max-w-[1280px] mx-auto flex flex-col gap-12 md:gap-14">
        <motion.div
          initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: reduceMotion ? 0 : 0.75, ease: EASE }}
          className="max-w-3xl"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-teal-400/85 mb-4">Our signature</p>
          <h2 className="font-heading text-[clamp(1.9rem,4.2vw,3.15rem)] font-medium tracking-[-0.03em] leading-[1.05]">
            India&apos;s only pocket hot-and-cold therapy—{' '}
            <span className="block mt-2 font-editorial italic font-normal text-[#9fd4c9]">
              one button. No ice bags. No boiling kettle.
            </span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: reduceMotion ? 0 : 0.8, ease: EASE }}
        >
          <a
            href={THERMACUBE_CHECKOUT_URL}
            target="_blank"
            rel="noopener noreferrer"
            ref={stageRef}
            className="relative mx-auto block w-full max-w-[440px] aspect-[4/5] rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-900/80 to-black/90 shadow-[0_40px_100px_-40px_rgba(0,0,0,0.9)] ring-1 ring-teal-400/10 overflow-hidden cursor-pointer transition-[box-shadow,transform] duration-300 hover:ring-teal-400/25 hover:shadow-[0_44px_110px_-36px_rgba(45,212,191,0.12)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-400/80"
            aria-label={`Shop ThermaCube — ${formatInr(THERMACUBE_PRICE_INR)}`}
          >
            {/* Chaos: ice, heat, figure */}
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              animate={
                resolved
                  ? { opacity: 0, filter: 'blur(12px)', scale: 0.92 }
                  : { opacity: 1, filter: 'blur(0px)', scale: 1 }
              }
              transition={{ duration: 0.85, ease: EASE }}
            >
              <motion.div
                className="relative z-[2]"
                animate={
                  resolved
                    ? { x: 0, y: 0 }
                    : { x: [0, -5, 5, -4, 4, 0], y: [0, 3, -2, 2, -3, 0] }
                }
                transition={
                  resolved ? { duration: 0 } : { duration: 2.2, repeat: Infinity, ease: 'easeInOut' }
                }
              >
                <FrustratedFigure className="w-[min(200px,48vw)] drop-shadow-[0_12px_32px_rgba(0,0,0,0.45)]" />
              </motion.div>
              <motion.div
                className="absolute z-[3] left-[8%] top-[18%]"
                animate={
                  resolved
                    ? { x: -90, y: -50, opacity: 0, rotate: -20 }
                    : { x: 0, y: 0, opacity: 1, rotate: 0 }
                }
                transition={{ duration: 0.75, ease: EASE }}
              >
                <IcePack className="relative w-20 h-14 motion-safe:animate-chaos-float" />
              </motion.div>
              <motion.div
                className="absolute z-[3] right-[10%] top-[22%]"
                animate={
                  resolved ? { x: 100, y: -30, opacity: 0, rotate: 18 } : { x: 0, y: 0, opacity: 1, rotate: 0 }
                }
                transition={{ duration: 0.75, ease: EASE, delay: 0.03 }}
              >
                <IcePack className="relative w-[4.5rem] h-16 motion-safe:animate-chaos-float [animation-delay:0.4s]" />
              </motion.div>
              <motion.div
                className="absolute z-[3] left-[12%] bottom-[20%]"
                animate={
                  resolved ? { x: -110, y: 60, opacity: 0, rotate: -12 } : { x: 0, y: 0, opacity: 1, rotate: 0 }
                }
                transition={{ duration: 0.75, ease: EASE, delay: 0.06 }}
              >
                <HotWaterBottle className="w-16 h-24 motion-safe:animate-chaos-float [animation-delay:0.2s]" />
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 flex gap-1 justify-center" aria-hidden>
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className="w-0.5 h-4 rounded-full bg-white/25 motion-safe:animate-steam-wisp"
                      style={{ animationDelay: `${i * 0.35}s` }}
                    />
                  ))}
                </div>
              </motion.div>
              <motion.div
                className="absolute z-[3] right-[8%] bottom-[24%]"
                animate={
                  resolved ? { x: 95, y: 55, opacity: 0, rotate: 25 } : { x: 0, y: 0, opacity: 1, rotate: 0 }
                }
                transition={{ duration: 0.75, ease: EASE, delay: 0.05 }}
              >
                <HotWaterBottle className="w-14 h-[5.25rem] motion-safe:animate-chaos-float opacity-90 [animation-delay:0.55s]" />
              </motion.div>
              <p className="absolute bottom-6 left-0 right-0 text-center font-mono text-[9px] uppercase tracking-[0.28em] text-zinc-500 z-[4] px-4">
                Freezer · kettle · guesswork
              </p>
            </motion.div>

            {/* Resolved: product */}
            <motion.div
              className="absolute inset-0 flex flex-col items-center justify-center p-6 md:p-8"
              initial={false}
              animate={
                resolved
                  ? { opacity: 1, scale: 1 }
                  : { opacity: 0, scale: 0.88 }
              }
              transition={{ duration: 0.8, ease: EASE, delay: resolved ? 0.12 : 0 }}
            >
              <div className="absolute top-5 right-5 text-teal-400/50" aria-hidden>
                <Sparkles className="w-7 h-7" strokeWidth={1} />
              </div>
              <div className="relative w-full max-h-[min(400px,54vh)] min-h-[220px] rounded-xl overflow-hidden border border-white/15 shadow-[0_0_48px_-8px_rgba(45,212,191,0.2)] bg-black">
                <motion.img
                  key={productKey}
                  src={productSrc}
                  alt=""
                  className="w-full h-full object-cover object-center"
                  initial={{ opacity: 0.2, scale: 1.04 }}
                  animate={
                    montageDone
                      ? { opacity: 1, scale: [1, 1.02, 1] }
                      : { opacity: 1, scale: 1 }
                  }
                  transition={
                    montageDone
                      ? { opacity: { duration: 0.35, ease: EASE }, scale: { duration: 5, repeat: Infinity, ease: 'easeInOut' } }
                      : { duration: 0.16, ease: EASE }
                  }
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" aria-hidden />
                {!montageDone && resolved && !reduceMotion ? (
                  <div
                    className="absolute bottom-3 left-3 right-3 flex justify-center gap-1 pointer-events-none"
                    aria-hidden
                  >
                    {montage.map((_, i) => (
                      <span
                        key={i}
                        className={`h-0.5 flex-1 max-w-6 rounded-full transition-colors duration-150 ${
                          i === montageIdx ? 'bg-teal-400/90' : 'bg-white/20'
                        }`}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
              <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.24em] text-teal-300/80 text-center">
                {montageDone || reduceMotion
                  ? 'One device · heat · cold · contrast'
                  : 'SprintFit photography · fast preview'}
              </p>
            </motion.div>
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: reduceMotion ? 0 : 0.7, ease: EASE, delay: reduceMotion ? 0 : 0.05 }}
          className="max-w-3xl"
        >
          <p className="text-zinc-400 text-base leading-relaxed max-w-lg mb-6">
            The messy ritual—freezer bags, boiling water, timers—is replaced by calibrated temperatures you control from a
            wearable that actually fits your day.
          </p>
          <p className="text-zinc-500 text-sm leading-relaxed max-w-lg mb-9 border-l-2 border-[#c9a962]/60 pl-5">
            ThermaCube is SprintFit&apos;s answer: serious thermal therapy in a cordless pad, engineered for real homes across
            India.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href={THERMACUBE_CHECKOUT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-white text-neutral-950 text-[11px] font-semibold uppercase tracking-[0.16em] hover:bg-[#c5ebe2] transition-colors duration-300"
            >
              Add to bag · {formatInr(THERMACUBE_PRICE_INR)}
              <ArrowUpRight className="w-3.5 h-3.5" aria-hidden />
            </a>
            <Link
              to="/products"
              className="inline-flex items-center px-6 py-3.5 border border-white/20 text-white text-[11px] font-semibold uppercase tracking-[0.16em] hover:bg-white/10 transition-colors"
            >
              Specifications
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
